import { Fragment, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  DEFAULT_STEP_MODELS,
  EFFORTS,
  Effort,
  GENERATION_STEPS,
  GenerationStep,
  MODEL_FAMILIES,
  ModelFamily,
  StepModelChoices,
} from '@quizdb/common';

/**
 * The one-line summary of `value`'s step models — one entry per `GENERATION_STEPS` step, joined
 * by `job.new.models.separator` — shared by this field's collapsed state and
 * `JobConfigsPage`'s table (QZ-D194).
 */
export const stepModelsSummary = (t: TFunction, value: StepModelChoices): string =>
  GENERATION_STEPS.map((step) =>
    t('job.new.models.summary', {
      step: t(`job.step.${step}`),
      family: t(`job.family.${value[step].family}`),
      effort: t(`job.effort.${value[step].effort}`),
    })
  ).join(t('job.new.models.separator'));

/** Props for `StepModelsField`. */
export interface StepModelsFieldProps {
  value: StepModelChoices;
  onChange: (value: StepModelChoices) => void;
  /** Opens the section when set, so a rejected value's error is seen (JSM-D2). */
  error?: string;
  disabled?: boolean;
}

/**
 * New Job's collapsible models section (JSM-D2). Collapsed on every mount to one summary line —
 * one entry per `GENERATION_STEPS` step, joined by `job.new.models.separator` — with a
 * `Change…` toggle. Expanded, the same bordered panel holds a family and an effort select per
 * step, in columns that line up whatever the step labels' length, and the header gains a reset
 * to `DEFAULT_STEP_MODELS` beside the toggle, which reads `Hide`. Plain `useState`, `aria-expanded` and
 * `aria-controls` from `useId()`, not Bootstrap collapse, as `OpenMaterial` does
 * (`MaterialPageLink.tsx`). A server rejection of the sent `stepModels` opens the section again
 * so the error is seen.
 */
export const StepModelsField = ({ value, onChange, error, disabled }: StepModelsFieldProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();

  useEffect(() => {
    if (error) {
      setOpen(true);
    }
  }, [error]);

  const summary = stepModelsSummary(t, value);

  const handleFamilyChange = (step: GenerationStep, family: ModelFamily) =>
    onChange({ ...value, [step]: { ...value[step], family } });

  const handleEffortChange = (step: GenerationStep, effort: Effort) =>
    onChange({ ...value, [step]: { ...value[step], effort } });

  return (
    <div className="qz-models">
      <div className="qz-models-head">
        <div className="flex-grow-1">
          <div className="fw-semibold">{t('job.new.models.label')}</div>
          {!open && <div className="qz-small qz-muted">{summary}</div>}
        </div>
        {open && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={disabled}
            onClick={() => onChange(DEFAULT_STEP_MODELS)}
          >
            {t('job.new.models.reset')}
          </button>
        )}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={disabled}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? t('job.new.models.hide') : t('job.new.models.change')}
        </button>
      </div>

      {open && (
        <div id={panelId} className="qz-models-steps">
          {GENERATION_STEPS.map((step) => {
            const familyId = `${panelId}-${step}-family`;
            const effortId = `${panelId}-${step}-effort`;
            return (
              <Fragment key={step}>
                <div className="fw-semibold">{t(`job.step.${step}`)}</div>
                <div>
                  <label htmlFor={familyId} className="visually-hidden">
                    {t('job.new.models.family', { step: t(`job.step.${step}`) })}
                  </label>
                  <select
                    id={familyId}
                    className="form-select form-select-sm"
                    disabled={disabled}
                    value={value[step].family}
                    onChange={(e) => handleFamilyChange(step, e.target.value as ModelFamily)}
                  >
                    {MODEL_FAMILIES.map((family) => (
                      <option key={family} value={family}>
                        {t(`job.family.${family}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor={effortId} className="visually-hidden">
                    {t('job.new.models.effort', { step: t(`job.step.${step}`) })}
                  </label>
                  <select
                    id={effortId}
                    className="form-select form-select-sm"
                    disabled={disabled}
                    value={value[step].effort}
                    onChange={(e) => handleEffortChange(step, e.target.value as Effort)}
                  >
                    {EFFORTS.map((effort) => (
                      <option key={effort} value={effort}>
                        {t(`job.effort.${effort}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </Fragment>
            );
          })}
        </div>
      )}

      {open && error && (
        <div id="job-step-models-error" className="invalid-feedback d-block">
          {error}
        </div>
      )}
    </div>
  );
};

export default StepModelsField;
